import React, { useState, useEffect, useCallback } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RepeatIcon from '@mui/icons-material/Repeat';
import { ToastContainer, toast } from 'react-toastify';
import { ConfirmacionEliminar } from '../elementos/eventos/avisos/VentanaEmergente';
import { fetchFestivos, crearFestivo, actualizarFestivo, eliminarFestivo } from '../../peticiones_API/PeticionesFestivos';
import './GestorFestivos.css';

function GestorFestivos() {
  const [festivos, setFestivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());
  const [filtroTipo, setFiltroTipo] = useState('');
  const [formData, setFormData] = useState({
    fecha: '',
    nombre: '',
    tipo: 'nacional',
    anio: new Date().getFullYear(),
    provincia: '',
    permanente: false
  });

  const confirmDialog = ConfirmacionEliminar();

  const cargarFestivos = useCallback(async () => {
    try {
      setLoading(true);
      const params = { anio: filtroAnio };
      if (filtroTipo) params.tipo = filtroTipo;
      const data = await fetchFestivos(params);
      setFestivos(data);
    } catch (error) {
      console.error('Error al cargar festivos:', error);
      toast.error('Error al cargar los festivos');
    } finally {
      setLoading(false);
    }
  }, [filtroAnio, filtroTipo]);

  useEffect(() => {
    cargarFestivos();
  }, [cargarFestivos]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await actualizarFestivo(editando.Id, formData);
        toast.success('Festivo actualizado correctamente');
      } else {
        await crearFestivo(formData);
        toast.success('Festivo creado correctamente');
      }
      setShowModal(false);
      resetForm();
      cargarFestivos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar el festivo');
    }
  };

  const handleEliminar = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'festivo');
    if (confirmado) {
      try {
        await eliminarFestivo(id);
        toast.success('Festivo eliminado correctamente');
        cargarFestivos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar el festivo');
      }
    }
  };

  const handleEditar = (festivo) => {
    setEditando(festivo);
    setFormData({
      fecha: festivo.fecha.split('T')[0],
      nombre: festivo.nombre,
      tipo: festivo.tipo,
      anio: festivo.anio,
      provincia: festivo.provincia || '',
      permanente: festivo.permanente || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      fecha: '',
      nombre: '',
      tipo: 'nacional',
      anio: new Date().getFullYear(),
      provincia: '',
      permanente: false
    });
    setEditando(null);
  };

  const getTipoChip = (tipo) => {
    const colores = {
      nacional: { bg: '#e91e63', label: 'Nacional' },
      empresa: { bg: '#9c27b0', label: 'Empresa' },
      provincial: { bg: '#673ab7', label: 'Provincial' }
    };
    const config = colores[tipo] || { bg: '#9e9e9e', label: tipo };
    return <Chip label={config.label} size="small" sx={{ backgroundColor: config.bg, color: 'white' }} />;
  };

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  if (loading) {
    return (
      <div className="gestor-festivos-loading">
        <div className="spinner"></div>
        <p>Cargando festivos...</p>
      </div>
    );
  }

  return (
    <div className="gestor-festivos">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385' }}>
          Gestion de Festivos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Año</InputLabel>
            <Select
              value={filtroAnio}
              label="Año"
              onChange={(e) => setFiltroAnio(e.target.value)}
            >
              {anios.map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filtroTipo}
              label="Tipo"
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="nacional">Nacional</MenuItem>
              <MenuItem value="empresa">Empresa</MenuItem>
              <MenuItem value="provincial">Provincial</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setShowModal(true); }}
            sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
          >
            Nuevo Festivo
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {festivos.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No hay festivos para el año {filtroAnio}
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Provincia</strong></TableCell>
                    <TableCell align="center"><strong>Permanente</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {festivos.map((festivo) => (
                    <TableRow key={festivo.Id} hover>
                      <TableCell>
                        {new Date(festivo.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long' })}
                      </TableCell>
                      <TableCell>{festivo.nombre}</TableCell>
                      <TableCell>{getTipoChip(festivo.tipo)}</TableCell>
                      <TableCell>{festivo.provincia || '-'}</TableCell>
                      <TableCell align="center">
                        {festivo.permanente && (
                          <Tooltip title="Se repite todos los años">
                            <RepeatIcon sx={{ color: '#4caf50' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleEditar(festivo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleEliminar(festivo.Id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          {editando ? 'Editar Festivo' : 'Nuevo Festivo'}
        </DialogTitle>
        <form onSubmit={handleGuardar}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Fecha"
              type="date"
              value={formData.fecha}
              onChange={(e) => {
                const fecha = e.target.value;
                const anio = fecha ? new Date(fecha).getFullYear() : formData.anio;
                setFormData({ ...formData, fecha, anio });
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo"
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                required
              >
                <MenuItem value="nacional">Nacional</MenuItem>
                <MenuItem value="empresa">Empresa</MenuItem>
                <MenuItem value="provincial">Provincial</MenuItem>
              </Select>
            </FormControl>
            {formData.tipo === 'provincial' && (
              <TextField
                fullWidth
                margin="normal"
                label="Provincia"
                value={formData.provincia}
                onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                required
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.permanente}
                  onChange={(e) => setFormData({ ...formData, permanente: e.target.checked })}
                  color="primary"
                />
              }
              label="Permanente (se repite todos los años)"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)} sx={{ color: '#333' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              {editando ? 'Guardar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default GestorFestivos;
